import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from "@angular/material/dialog";
import { VersioningService } from "src/app/Services/versioning.service";
import { VersionSummary } from "src/app/models/versioning.model";
import { ToastrService } from "ngx-toastr";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { ViewflowComponent } from "../viewflow/viewflow.component";
import { ViewBundleComponent } from "../view-bundle/view-bundle.component";
import { BundleService } from "src/app/Services/bundleservice.service";
import { FlowQueryModel } from "src/app/models/flowquerymodel";
import { VersionRestoreConfirmDialogComponent } from "./version-restore-confirm-dialog.component";
import { FlowServiceService } from "src/app/Services/flow-service.service";

export interface VersionHistoryDialogData {
  entityType: "flow" | "bundle";
  logicalId: string;
  canManageVersions?: boolean;
}

/** Emitted when dialog closes after a successful restore or delete. */
export interface VersionHistoryDialogResult {
  action: "restore" | "delete";
  version: number;
  logicalId: string;
  entityType: "flow" | "bundle";
}

@Component({
  selector: "app-version-history-panel",
  templateUrl: "./version-history-panel.component.html",
  styleUrls: ["./version-history-panel.component.scss"],
})
export class VersionHistoryPanelComponent implements OnInit {
  versions: VersionSummary[] = [];
  loading = false;
  error: string | null = null;

  previewVersion: any = null;
  previewLoading = false;

  currentRestoreVersion: number | null = null;
  currentDeleteVersion: number | null = null;

  // Comparison selection + summary
  selectedVersions: VersionSummary[] = [];
  diffSummary: string[] = [];
  diffLoading = false;
  diffError: string | null = null;
  private readonly videoFileExtensions: string[] = [
    "mp4",
    "mov",
    "wmv",
    "flv",
    "webm",
    "mkv",
    "avi",
    "mts",
    "m2ts",
    "ts",
    "mpg",
    "mpeg",
    "ogg",
    "m4v",
  ];

  constructor(
    private dialogRef: MatDialogRef<VersionHistoryPanelComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VersionHistoryDialogData,
    private versioningService: VersioningService,
    private bundleService: BundleService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private flowService: FlowServiceService
  ) {}

  get canDeleteVersions(): boolean {
    return !!this.data?.canManageVersions;
  }

  ngOnInit(): void {
    if (!this.data?.logicalId) {
      this.error = "Missing logical id.";
      return;
    }
    this.loadHistory();
  }

  loadHistory(): void {
    this.loading = true;
    this.error = null;

    const source =
      this.data.entityType === "flow"
        ? this.versioningService.getFlowVersionHistory(this.data.logicalId)
        : this.versioningService.getBundleVersionHistory(this.data.logicalId);

    source.subscribe({
      next: (versions) => {
        const raw = versions || [];

        // De-dupe by version number, preferring the entry marked current (if any),
        // otherwise prefer the most recent createdAt.
        const byVersion = new Map<number, VersionSummary>();
        for (const v of raw) {
          const existing = byVersion.get(v.version);
          if (!existing) {
            byVersion.set(v.version, v);
            continue;
          }

          const existingIsCurrent = !!(existing as any).isCurrent;
          const nextIsCurrent = !!(v as any).isCurrent;
          if (!existingIsCurrent && nextIsCurrent) {
            byVersion.set(v.version, v);
            continue;
          }

          const existingDate = new Date((existing as any).versionCreatedAt || 0).getTime();
          const nextDate = new Date((v as any).versionCreatedAt || 0).getTime();
          if (nextDate > existingDate) {
            byVersion.set(v.version, v);
          }
        }

        this.versions = Array.from(byVersion.values()).sort(
          (a, b) => (b.version || 0) - (a.version || 0)
        );

        // Normalize "Current" so it always matches the latest version when API data is inconsistent.
        // If the API returns a wrong/old isCurrent flag (e.g. v1), we override to the highest version.
        const latest = this.versions[0];
        const anyMarkedCurrent = this.versions.some((v) => (v as any).isCurrent);
        if (latest && (!anyMarkedCurrent || (latest as any).isCurrent !== true)) {
          this.versions = this.versions.map((v) => ({
            ...v,
            isCurrent: v.version === latest.version,
          }));
        } else {
          // If multiple are marked current, keep only the latest one as current.
          const latestCurrent = this.versions.find((v) => (v as any).isCurrent) || latest;
          if (latestCurrent) {
            this.versions = this.versions.map((v) => ({
              ...v,
              isCurrent: v.version === latestCurrent.version,
            }));
          }
        }

        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error || "Failed to load version history.";
        this.loading = false;
      },
    });
  }

  /** Always returns the two selected versions sorted oldest → newest for display. */
  get sortedSelectedVersions(): VersionSummary[] {
    return [...this.selectedVersions].sort((a, b) => (a.version || 0) - (b.version || 0));
  }

  isSelected(version: VersionSummary): boolean {
    return this.selectedVersions.some((v) => v.version === version.version);
  }

  onToggleSelection(version: VersionSummary, checked: boolean): void {
    if (checked) {
      // Add and keep only latest two selections
      this.selectedVersions = [
        ...this.selectedVersions.filter((v) => v.version !== version.version),
        version,
      ].slice(-2);
    } else {
      this.selectedVersions = this.selectedVersions.filter(
        (v) => v.version !== version.version
      );
    }

    if (this.selectedVersions.length === 2) {
      this.buildDiffSummary();
    } else {
      this.diffSummary = [];
      this.diffError = null;
    }
  }

  /** Ordered flow row keys / ids as stored in the bundle version snapshot. */
  private getBundleOrderedFlowIds(mapped: any): string[] {
    const flows = mapped?.flows;
    if (Array.isArray(flows) && flows.length) {
      return flows
        .map((f: any) => `${f?.rowKey ?? f?.RowKey ?? ""}`.trim())
        .filter(Boolean);
    }
    const ids = mapped?.flowIds ?? mapped?.FlowIds;
    if (Array.isArray(ids)) {
      return ids.map((id: any) => `${id ?? ""}`.trim()).filter(Boolean);
    }
    return [];
  }

  private bundleFlowMultisetEqual(idsA: string[], idsB: string[]): boolean {
    if (idsA.length !== idsB.length) {
      return false;
    }
    const counts = new Map<string, number>();
    for (const id of idsA) {
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    for (const id of idsB) {
      const c = counts.get(id);
      if (!c) {
        return false;
      }
      if (c === 1) {
        counts.delete(id);
      } else {
        counts.set(id, c - 1);
      }
    }
    return counts.size === 0;
  }

  private bundleFlowDisplayName(mapped: any, flowId: string): string {
    const flows = mapped?.flows;
    const f = Array.isArray(flows)
      ? flows.find(
          (x: any) => `${x?.rowKey ?? x?.RowKey ?? ""}`.trim() === flowId
        )
      : null;
    const t = (f?.title || f?.Title || "").trim();
    const placeholder = `Flow ${flowId}`;
    if (t && t !== placeholder) {
      return t;
    }
    return placeholder;
  }

  /** Strip HTML tags and decode common entities to plain text for diff comparison. */
  private stripHtml(html: string): string {
    if (!html || typeof html !== 'string') return '';
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Returns a readable "before → after" snippet centred on the first character
   * that differs between two plain-text strings.  Returns an empty string when
   * the texts are identical.
   */
  private getChangeSnippet(aText: string, bText: string): string {
    if (!aText || !bText) return '';
    let start = 0;
    const minLen = Math.min(aText.length, bText.length);
    while (start < minLen && aText[start] === bText[start]) start++;
    if (start === aText.length && start === bText.length) return '';

    let aEnd = aText.length - 1;
    let bEnd = bText.length - 1;
    while (aEnd > start && bEnd > start && aText[aEnd] === bText[bEnd]) {
      aEnd--;
      bEnd--;
    }

    const ctx = 25;
    const ctxStart = Math.max(0, start - ctx);
    const aCtxEnd = Math.min(aText.length, aEnd + ctx);
    const bCtxEnd = Math.min(bText.length, bEnd + ctx);
    const prefix = ctxStart > 0 ? '…' : '';
    const aSnip = prefix + aText.substring(ctxStart, aCtxEnd) + (aCtxEnd < aText.length ? '…' : '');
    const bSnip = prefix + bText.substring(ctxStart, bCtxEnd) + (bCtxEnd < bText.length ? '…' : '');
    return `"${aSnip}" → "${bSnip}"`;
  }

  /**
   * Human-readable diff for two bundle versions (snapshot has metadata + ordered flow ids).
   */
  private compareBundleVersions(
    rawA: any,
    rawB: any,
    versionA: number,
    versionB: number,
    labelA: string,
    labelB: string
  ): string[] {
    const bullets: string[] = [];
    const a = this.mapBundlePreviewModel(rawA, versionA);
    const b = this.mapBundlePreviewModel(rawB, versionB);

    if ((a.title || "").trim() !== (b.title || "").trim()) {
      bullets.push(
        `Bundle title changed from "${a.title || "Untitled"}" (${labelA}) to "${
          b.title || "Untitled"
        }" (${labelB}).`
      );
    }

    if ((a.description || "").trim() !== (b.description || "").trim()) {
      const aLen = (a.description || "").length;
      const bLen = (b.description || "").length;
      if (aLen && bLen) {
        bullets.push(
          `Bundle description was updated (${labelA}: ${aLen} chars → ${labelB}: ${bLen} chars).`
        );
      } else if (!aLen && bLen) {
        bullets.push(`Bundle description was added in ${labelB}.`);
      } else if (aLen && !bLen) {
        bullets.push(`Bundle description was removed in ${labelB}.`);
      } else {
        bullets.push("Bundle description was changed.");
      }
    }

    const aPrice = a.price ?? a.Price;
    const bPrice = b.price ?? b.Price;
    if (aPrice != null || bPrice != null) {
      if (aPrice !== bPrice) {
        bullets.push(
          `Bundle price changed from ${
            aPrice != null ? "£" + aPrice : "not set"
          } (${labelA}) to ${bPrice != null ? "£" + bPrice : "not set"} (${labelB}).`
        );
      }
    }

    const urlA = `${a.url || ""}`.trim();
    const urlB = `${b.url || ""}`.trim();
    if (urlA !== urlB) {
      bullets.push(
        `Bundle cover media URL changed between ${labelA} and ${labelB}.`
      );
    }

    const idsA = this.getBundleOrderedFlowIds(a);
    const idsB = this.getBundleOrderedFlowIds(b);
    const setA = new Set(idsA);
    const setB = new Set(idsB);

    const removedIds = idsA.filter((id) => !setB.has(id));
    const addedIds = idsB.filter((id) => !setA.has(id));

    if (removedIds.length) {
      const names = removedIds.map((id) => this.bundleFlowDisplayName(a, id));
      bullets.push(
        `Flows removed (in ${labelA}, not in ${labelB}): ${names.join(
          "; "
        )}.`
      );
    }

    if (addedIds.length) {
      const names = addedIds.map((id) => this.bundleFlowDisplayName(b, id));
      bullets.push(
        `Flows added (in ${labelB}, not in ${labelA}): ${names.join("; ")}.`
      );
    }

    const sameFlows =
      removedIds.length === 0 &&
      addedIds.length === 0 &&
      this.bundleFlowMultisetEqual(idsA, idsB);
    const sameOrder =
      sameFlows &&
      idsA.length === idsB.length &&
      idsA.every((id, i) => id === idsB[i]);

    if (sameFlows && !sameOrder && idsA.length > 1) {
      bullets.push(
        `Flow order changed: the same flows appear in a different sequence between ${labelA} and ${labelB}.`
      );
    }

    if (!bullets.length) {
      bullets.push(
        "No differences detected between the selected bundle versions."
      );
    }

    return bullets;
  }

  private buildDiffSummary(): void {
    if (this.selectedVersions.length !== 2) {
      return;
    }

    // Always compare older → newer regardless of selection order
    const [first, second] = [...this.selectedVersions].sort(
      (a, b) => (a.version || 0) - (b.version || 0)
    );
    this.diffLoading = true;
    this.diffError = null;
    this.diffSummary = [];

    const firstReq =
      this.data.entityType === "flow"
        ? this.versioningService.getFlowByVersion(
            this.data.logicalId,
            first.version
          )
        : this.versioningService.getBundleByVersion(
            this.data.logicalId,
            first.version
          );

    const secondReq =
      this.data.entityType === "flow"
        ? this.versioningService.getFlowByVersion(
            this.data.logicalId,
            second.version
          )
        : this.versioningService.getBundleByVersion(
            this.data.logicalId,
            second.version
          );

    // Fetch live flow to surface collaborated/sponsored data not stored in version snapshots
    const liveFlowReq = this.data.entityType === "flow"
      ? this.flowService.getFlowById(new FlowQueryModel({
          FlowId: this.data.logicalId,
          userEmail: this.getCurrentUserEmail(),
        })).pipe(catchError(() => of(null)))
      : of(null);

    forkJoin([firstReq, secondReq, liveFlowReq]).subscribe({
      next: ([a, b, livePayload]) => {
        const fromLabel = `v${first.version}`;
        const toLabel = `v${second.version}`;

        if (this.data.entityType === "bundle") {
          const bullets = this.compareBundleVersions(
            a,
            b,
            first.version,
            second.version,
            fromLabel,
            toLabel
          );
          this.diffSummary = bullets;
          this.diffLoading = false;
          return;
        }

        const live = (livePayload as any)?.flows ?? (livePayload as any)?.Flows ?? null;

        const bullets: string[] = [];

        // 1) Title
        if ((a.title || "").trim() !== (b.title || "").trim()) {
          bullets.push(
            `Title changed from "${a.title || "Untitled"}" (${fromLabel}) to "${b.title || "Untitled"}" (${toLabel}).`
          );
        }

        // 2) Description
        const aDescRaw = (a.description || "").trim();
        const bDescRaw = (b.description || "").trim();
        if (aDescRaw !== bDescRaw) {
          const aPlain = this.stripHtml(aDescRaw);
          const bPlain = this.stripHtml(bDescRaw);
          if (!aPlain && bPlain) {
            bullets.push(`Flow description was added in ${toLabel}.`);
          } else if (aPlain && !bPlain) {
            bullets.push(`Flow description was removed in ${toLabel}.`);
          } else {
            const snippet = this.getChangeSnippet(aPlain, bPlain);
            bullets.push(
              snippet
                ? `Flow description was updated — ${snippet}.`
                : `Flow description was updated (${aPlain.length} → ${bPlain.length} chars).`
            );
          }
        }

        // 3) Cover media URL
        const aFlowUrl = (a.url || a.Url || a.mediaUrl || a.MediaUrl || a.filePath || a.FilePath || "").trim();
        const bFlowUrl = (b.url || b.Url || b.mediaUrl || b.MediaUrl || b.filePath || b.FilePath || "").trim();
        if (aFlowUrl !== bFlowUrl) {
          if (!aFlowUrl && bFlowUrl) {
            bullets.push(`Cover media was added in ${toLabel}.`);
          } else if (aFlowUrl && !bFlowUrl) {
            bullets.push(`Cover media was removed in ${toLabel}.`);
          } else {
            bullets.push(`Cover media was changed between ${fromLabel} and ${toLabel}.`);
          }
        }

        // 4) Cover poster / thumbnail
        const aFlowPoster = (a.videoThumbNailUrl || a.VideoThumbNailUrl || a.videoThumbnailUrl || a.posterUrl || "").trim();
        const bFlowPoster = (b.videoThumbNailUrl || b.VideoThumbNailUrl || b.videoThumbnailUrl || b.posterUrl || "").trim();
        if (aFlowPoster !== bFlowPoster) {
          if (!aFlowPoster && bFlowPoster) {
            bullets.push(`Cover video thumbnail was added in ${toLabel}.`);
          } else if (aFlowPoster && !bFlowPoster) {
            bullets.push(`Cover video thumbnail was removed in ${toLabel}.`);
          } else {
            bullets.push(`Cover video thumbnail was changed.`);
          }
        }

        // 5) Cover file name (FileName / fileName)
        const aFlowFile = (a.fileName || a.FileName || "").trim();
        const bFlowFile = (b.fileName || b.FileName || "").trim();
        if (aFlowFile !== bFlowFile) {
          if (!aFlowFile && bFlowFile) {
            bullets.push(`Cover file was added ("${bFlowFile}") in ${toLabel}.`);
          } else if (aFlowFile && !bFlowFile) {
            bullets.push(`Cover file was removed ("${aFlowFile}") in ${toLabel}.`);
          } else {
            bullets.push(`Cover file changed from "${aFlowFile}" to "${bFlowFile}".`);
          }
        }

        // 6) Cover safety-critical flag (CoverIsSafetyCritical / coverIsSafetyCritical)
        const aCoverSafe = !!(a.CoverIsSafetyCritical ?? a.coverIsSafetyCritical);
        const bCoverSafe = !!(b.CoverIsSafetyCritical ?? b.coverIsSafetyCritical);
        if (aCoverSafe !== bCoverSafe) {
          bullets.push(
            `Cover safety-critical status changed from ${aCoverSafe ? "ON" : "OFF"} to ${bCoverSafe ? "ON" : "OFF"}.`
          );
        }

        // Cover safety instructions title
        const aCoverSafeTitle = (a.CoverSafetyInstructionsTitle || a.coverSafetyInstructionsTitle || "").trim();
        const bCoverSafeTitle = (b.CoverSafetyInstructionsTitle || b.coverSafetyInstructionsTitle || "").trim();
        if (aCoverSafeTitle !== bCoverSafeTitle) {
          if (!aCoverSafeTitle && bCoverSafeTitle) {
            bullets.push(`Cover safety instructions title was added in ${toLabel}.`);
          } else if (aCoverSafeTitle && !bCoverSafeTitle) {
            bullets.push(`Cover safety instructions title was removed in ${toLabel}.`);
          } else {
            bullets.push(`Cover safety instructions title changed from "${aCoverSafeTitle}" to "${bCoverSafeTitle}".`);
          }
        }

        // Cover safety instructions description
        const aCoverSafeDesc = (a.CoverSafetyInstructionsDescription || a.coverSafetyInstructionsDescription || "").trim();
        const bCoverSafeDesc = (b.CoverSafetyInstructionsDescription || b.coverSafetyInstructionsDescription || "").trim();
        if (aCoverSafeDesc !== bCoverSafeDesc) {
          if (!aCoverSafeDesc && bCoverSafeDesc) {
            bullets.push(`Cover safety instructions description was added in ${toLabel}.`);
          } else if (aCoverSafeDesc && !bCoverSafeDesc) {
            bullets.push(`Cover safety instructions description was removed in ${toLabel}.`);
          } else {
            const snippet = this.getChangeSnippet(this.stripHtml(aCoverSafeDesc), this.stripHtml(bCoverSafeDesc));
            bullets.push(
              snippet
                ? `Cover safety instructions description was updated — ${snippet}.`
                : `Cover safety instructions description was updated.`
            );
          }
        }

        // 7) Access type
        const aAccess = (a.accessType || a.AccessType || "").trim();
        const bAccess = (b.accessType || b.AccessType || "").trim();
        if (aAccess.toLowerCase() !== bAccess.toLowerCase() && (aAccess || bAccess)) {
          bullets.push(
            `Access type changed from "${aAccess || "not set"}" (${fromLabel}) to "${bAccess || "not set"}" (${toLabel}).`
          );
        }

        // 6) Paid / free
        const aIsPaid = !!(a.isPaid ?? a.IsPaid);
        const bIsPaid = !!(b.isPaid ?? b.IsPaid);
        if (aIsPaid !== bIsPaid) {
          bullets.push(`Flow changed from ${aIsPaid ? "paid" : "free"} to ${bIsPaid ? "paid" : "free"}.`);
        }

        // 7) Search keywords (SearchKeyWords / searchKeyWords — comma-separated string)
        const parseKeywords = (raw: any): string[] => {
          const str = `${raw?.SearchKeyWords ?? raw?.searchKeyWords ?? ""}`.trim();
          if (!str) return [];
          return str.split(",").map((k) => k.trim().toLowerCase()).filter(Boolean);
        };
        const aKeywords = parseKeywords(a);
        const bKeywords = parseKeywords(b);
        const aKwNorm = [...aKeywords].sort().join("|");
        const bKwNorm = [...bKeywords].sort().join("|");
        if (aKwNorm !== bKwNorm) {
          const aKwSet = new Set(aKeywords);
          const bKwSet = new Set(bKeywords);
          const addedKw = bKeywords.filter((k) => !aKwSet.has(k));
          const removedKw = aKeywords.filter((k) => !bKwSet.has(k));
          if (addedKw.length) bullets.push(`Keywords added in ${toLabel}: ${addedKw.join(", ")}.`);
          if (removedKw.length) bullets.push(`Keywords removed in ${toLabel}: ${removedKw.join(", ")}.`);
          if (!addedKw.length && !removedKw.length) bullets.push("Keywords were updated.");
        }

        // 9) Supporting hyperlinks
        const aLinks: any[] = Array.isArray(a.flowSupportingDocumentsHyperLinkModels || a.FlowSupportingDocumentsHyperLinkModels)
          ? (a.flowSupportingDocumentsHyperLinkModels || a.FlowSupportingDocumentsHyperLinkModels) : [];
        const bLinks: any[] = Array.isArray(b.flowSupportingDocumentsHyperLinkModels || b.FlowSupportingDocumentsHyperLinkModels)
          ? (b.flowSupportingDocumentsHyperLinkModels || b.FlowSupportingDocumentsHyperLinkModels) : [];
        if (JSON.stringify(aLinks) !== JSON.stringify(bLinks)) {
          const linkDiff = bLinks.length - aLinks.length;
          if (linkDiff > 0) {
            bullets.push(`${linkDiff} supporting hyperlink(s) added in ${toLabel}.`);
          } else if (linkDiff < 0) {
            bullets.push(`${Math.abs(linkDiff)} supporting hyperlink(s) removed in ${toLabel}.`);
          } else {
            bullets.push("Supporting hyperlinks were updated.");
          }
        }

        // 10) Supporting documents (files)
        const aDocs: any[] = Array.isArray(a.flowSupportingDocumentsDocumentModels || a.FlowSupportingDocumentsDocumentModels)
          ? (a.flowSupportingDocumentsDocumentModels || a.FlowSupportingDocumentsDocumentModels) : [];
        const bDocs: any[] = Array.isArray(b.flowSupportingDocumentsDocumentModels || b.FlowSupportingDocumentsDocumentModels)
          ? (b.flowSupportingDocumentsDocumentModels || b.FlowSupportingDocumentsDocumentModels) : [];
        if (JSON.stringify(aDocs) !== JSON.stringify(bDocs)) {
          const docDiff = bDocs.length - aDocs.length;
          if (docDiff > 0) {
            bullets.push(`${docDiff} supporting document(s) added in ${toLabel}.`);
          } else if (docDiff < 0) {
            bullets.push(`${Math.abs(docDiff)} supporting document(s) removed in ${toLabel}.`);
          } else {
            bullets.push("Supporting document(s) were updated.");
          }
        }

        // 11) Components / items required (Components / components)
        const aComponents: any[] = Array.isArray(a.components || a.Components) ? (a.components || a.Components) : [];
        const bComponents: any[] = Array.isArray(b.components || b.Components) ? (b.components || b.Components) : [];
        if (JSON.stringify(aComponents) !== JSON.stringify(bComponents)) {
          // API returns the component name in "itemkey" (all-lowercase, single word)
          const getCompName = (c: any) =>
            (c?.itemkey || c?.Itemkey || c?.itemName || c?.ItemName || c?.name || c?.Name || c?.componentName || c?.ComponentName || "").trim();
          const aCompNames = aComponents.map(getCompName).filter(Boolean);
          const bCompNames = bComponents.map(getCompName).filter(Boolean);
          const aCompSet = new Set(aCompNames);
          const bCompSet = new Set(bCompNames);
          const addedComps = bCompNames.filter((n) => !aCompSet.has(n));
          const removedComps = aCompNames.filter((n) => !bCompSet.has(n));
          if (addedComps.length) {
            bullets.push(`Items required added in ${toLabel}: ${addedComps.join(", ")}.`);
          }
          if (removedComps.length) {
            bullets.push(`Items required removed in ${toLabel}: ${removedComps.join(", ")}.`);
          }
          if (!addedComps.length && !removedComps.length) {
            const compDiff = bComponents.length - aComponents.length;
            if (compDiff > 0) {
              bullets.push(`${compDiff} item(s) required added in ${toLabel}.`);
            } else if (compDiff < 0) {
              bullets.push(`${Math.abs(compDiff)} item(s) required removed in ${toLabel}.`);
            } else {
              bullets.push("Items required were updated.");
            }
          }
        }

        // 12) Steps — full field-by-field comparison for every step
        const aSteps: any[] = a.steps || a.Steps || [];
        const bSteps: any[] = b.steps || b.Steps || [];

        if (aSteps.length !== bSteps.length) {
          bullets.push(
            `Number of steps changed from ${aSteps.length} in ${fromLabel} to ${bSteps.length} in ${toLabel}.`
          );
        }

        // Detect step reordering (same set of step identifiers, different sequence)
        if (aSteps.length === bSteps.length && aSteps.length > 1) {
          const getStepKey = (s: any) =>
            (s.stepTitle || s.title || s.Title || s.id || s.Id || s.rowKey || s.RowKey || "").trim();
          const aTitles = aSteps.map(getStepKey).filter(Boolean);
          const bTitles = bSteps.map(getStepKey).filter(Boolean);
          const aSet = new Set(aTitles);
          const bSet = new Set(bTitles);
          if (
            aTitles.length > 0 &&
            JSON.stringify(aTitles) !== JSON.stringify(bTitles) &&
            aTitles.every((t) => bSet.has(t)) &&
            bTitles.every((t) => aSet.has(t))
          ) {
            bullets.push(`Steps were reordered between ${fromLabel} and ${toLabel}.`);
          }
        }

        const minLen = Math.min(aSteps.length, bSteps.length);

        for (let i = 0; i < minLen; i++) {
          const sa = aSteps[i];
          const sb = bSteps[i];
          const stepLabel = `Step ${i + 1}`;

          // Title
          const aTitle = (sa.stepTitle || sa.title || sa.Title || "").trim();
          const bTitle = (sb.stepTitle || sb.title || sb.Title || "").trim();
          if (aTitle !== bTitle) {
            bullets.push(
              `${stepLabel} title changed from "${aTitle || "Untitled"}" to "${bTitle || "Untitled"}".`
            );
          }

          // Description / body text — with inline snippet
          const aDesc = (sa.description || sa.Description || sa.stepDescription || "").trim();
          const bDesc = (sb.description || sb.Description || sb.stepDescription || "").trim();
          if (aDesc !== bDesc) {
            const aDescPlain = this.stripHtml(aDesc);
            const bDescPlain = this.stripHtml(bDesc);
            if (!aDescPlain && bDescPlain) {
              bullets.push(`${stepLabel} description was added in ${toLabel}.`);
            } else if (aDescPlain && !bDescPlain) {
              bullets.push(`${stepLabel} description was removed in ${toLabel}.`);
            } else {
              const snippet = this.getChangeSnippet(aDescPlain, bDescPlain);
              bullets.push(
                snippet
                  ? `${stepLabel} description was updated — ${snippet}.`
                  : `${stepLabel} description was updated (${aDescPlain.length} → ${bDescPlain.length} chars).`
              );
            }
          }

          // Media URL (image/video)
          const aUrl = (sa.url || sa.Url || sa.mediaUrl || sa.MediaUrl || "").trim();
          const bUrl = (sb.url || sb.Url || sb.mediaUrl || sb.MediaUrl || "").trim();
          if (aUrl !== bUrl) {
            if (!aUrl && bUrl) {
              bullets.push(`${stepLabel} media was added in ${toLabel}.`);
            } else if (aUrl && !bUrl) {
              bullets.push(`${stepLabel} media was removed in ${toLabel}.`);
            } else {
              bullets.push(`${stepLabel} media file was changed.`);
            }
          }

          // Poster / thumbnail URL
          const aPoster = (sa.posterUrl || sa.videoThumbNailUrl || sa.VideoThumbNailUrl || sa.videoThumbnailUrl || "").trim();
          const bPoster = (sb.posterUrl || sb.videoThumbNailUrl || sb.VideoThumbNailUrl || sb.videoThumbnailUrl || "").trim();
          if (aPoster !== bPoster) {
            if (!aPoster && bPoster) {
              bullets.push(`${stepLabel} video thumbnail was added in ${toLabel}.`);
            } else if (aPoster && !bPoster) {
              bullets.push(`${stepLabel} video thumbnail was removed in ${toLabel}.`);
            } else {
              bullets.push(`${stepLabel} video thumbnail was changed.`);
            }
          }

          // Attached file name
          const aFileName = (sa.stepFileName || sa.fileName || sa.FileName || "").trim();
          const bFileName = (sb.stepFileName || sb.fileName || sb.FileName || "").trim();
          if (aFileName !== bFileName) {
            if (!aFileName && bFileName) {
              bullets.push(`${stepLabel} attached file was added ("${bFileName}") in ${toLabel}.`);
            } else if (aFileName && !bFileName) {
              bullets.push(`${stepLabel} attached file was removed ("${aFileName}") in ${toLabel}.`);
            } else {
              bullets.push(`${stepLabel} attached file changed from "${aFileName}" to "${bFileName}".`);
            }
          }

          // Safety-critical flag
          const aSafe = !!(sa.isSafetyCritical ?? sa.IsSafetyCritical);
          const bSafe = !!(sb.isSafetyCritical ?? sb.IsSafetyCritical);
          if (aSafe !== bSafe) {
            bullets.push(
              `${stepLabel} safety-critical status changed from ${aSafe ? "ON" : "OFF"} to ${bSafe ? "ON" : "OFF"}.`
            );
          }

          // Safety instructions title
          const aSafeTitle = (sa.safetyInstructionsTitle || sa.SafetyInstructionsTitle || "").trim();
          const bSafeTitle = (sb.safetyInstructionsTitle || sb.SafetyInstructionsTitle || "").trim();
          if (aSafeTitle !== bSafeTitle) {
            if (!aSafeTitle && bSafeTitle) {
              bullets.push(`${stepLabel} safety instructions title was added in ${toLabel}.`);
            } else if (aSafeTitle && !bSafeTitle) {
              bullets.push(`${stepLabel} safety instructions title was removed in ${toLabel}.`);
            } else {
              bullets.push(`${stepLabel} safety instructions title changed from "${aSafeTitle}" to "${bSafeTitle}".`);
            }
          }

          // Safety instructions description — with inline snippet
          const aSafeDesc = (sa.safetyInstructionsDescription || sa.SafetyInstructionsDescription || "").trim();
          const bSafeDesc = (sb.safetyInstructionsDescription || sb.SafetyInstructionsDescription || "").trim();
          if (aSafeDesc !== bSafeDesc) {
            if (!aSafeDesc && bSafeDesc) {
              bullets.push(`${stepLabel} safety instructions description was added in ${toLabel}.`);
            } else if (aSafeDesc && !bSafeDesc) {
              bullets.push(`${stepLabel} safety instructions description was removed in ${toLabel}.`);
            } else {
              const snippet = this.getChangeSnippet(this.stripHtml(aSafeDesc), this.stripHtml(bSafeDesc));
              bullets.push(
                snippet
                  ? `${stepLabel} safety instructions description was updated — ${snippet}.`
                  : `${stepLabel} safety instructions description was updated (${aSafeDesc.length} → ${bSafeDesc.length} chars).`
              );
            }
          }

          // Tagged items — granular: show which names were added or removed
          const getTagName = (tag: any): string =>
            (tag?.itemName || tag?.ItemName || tag?.itemkey || tag?.itemKey || tag?.ItemKey ||
             tag?.name || tag?.Name || tag?.title || tag?.Title || tag?.productName || tag?.ProductName || "").trim();
          const aTagged: any[] = Array.isArray(sa.taggedItems || sa.TaggedItems) ? (sa.taggedItems || sa.TaggedItems) : [];
          const bTagged: any[] = Array.isArray(sb.taggedItems || sb.TaggedItems) ? (sb.taggedItems || sb.TaggedItems) : [];
          if (JSON.stringify(aTagged) !== JSON.stringify(bTagged)) {
            const aTagNames = aTagged.map(getTagName).filter(Boolean);
            const bTagNames = bTagged.map(getTagName).filter(Boolean);
            const aTagSet = new Set(aTagNames);
            const bTagSet = new Set(bTagNames);
            const addedTags = bTagNames.filter((n) => !aTagSet.has(n));
            const removedTags = aTagNames.filter((n) => !bTagSet.has(n));
            if (addedTags.length) {
              bullets.push(`${stepLabel}: tagged item(s) added — ${addedTags.join(", ")}.`);
            }
            if (removedTags.length) {
              bullets.push(`${stepLabel}: tagged item(s) removed — ${removedTags.join(", ")}.`);
            }
            if (!addedTags.length && !removedTags.length) {
              const tagDiff = bTagged.length - aTagged.length;
              if (tagDiff !== 0) {
                bullets.push(
                  tagDiff > 0
                    ? `${stepLabel}: ${tagDiff} tagged item(s) added in ${toLabel}.`
                    : `${stepLabel}: ${Math.abs(tagDiff)} tagged item(s) removed in ${toLabel}.`
                );
              } else {
                bullets.push(`${stepLabel}: tagged item details were updated.`);
              }
            }
          }
        }

        // Name each removed step explicitly
        for (let i = minLen; i < aSteps.length; i++) {
          const s = aSteps[i];
          const stepTitle = (s.stepTitle || s.title || s.Title || "").trim();
          bullets.push(
            stepTitle
              ? `Step ${i + 1} ("${stepTitle}") was removed in ${toLabel}.`
              : `Step ${i + 1} was removed in ${toLabel}.`
          );
        }

        // Name each added step explicitly
        for (let i = minLen; i < bSteps.length; i++) {
          const s = bSteps[i];
          const stepTitle = (s.stepTitle || s.title || s.Title || "").trim();
          bullets.push(
            stepTitle
              ? `Step ${i + 1} ("${stepTitle}") was added in ${toLabel}.`
              : `Step ${i + 1} was added in ${toLabel}.`
          );
        }

        // 13) Price (Price / price — PascalCase stored in snapshot, camelCase in GET response)
        const aPrice = a.Price ?? a.price;
        const bPrice = b.Price ?? b.price;
        if (aPrice != null || bPrice != null) {
          const aIsPaidCtx = !!(a.IsPaid ?? a.isPaid);
          const bIsPaidCtx = !!(b.IsPaid ?? b.isPaid);
          const aPriceLabel = aPrice != null && aIsPaidCtx && aPrice > 0 ? "£" + aPrice : "free";
          const bPriceLabel = bPrice != null && bIsPaidCtx && bPrice > 0 ? "£" + bPrice : "free";
          if (aPriceLabel !== bPriceLabel) {
            bullets.push(
              `Price changed from ${aPriceLabel} (${fromLabel}) to ${bPriceLabel} (${toLabel}).`
            );
          }
        }

        // 14) Duration — use stored unit from each snapshot independently,
        //     fall back to live flow's unit (for old snapshots that predate unit storage), then 'Mins'
        const aDuration = a.Duration ?? a.duration;
        const bDuration = b.Duration ?? b.duration;
        const liveDurationUnit = ((live?.durationUnit || live?.DurationUnit) as string || "Mins").trim();
        const aDurationUnit = ((a.durationUnit || a.DurationUnit || liveDurationUnit) as string).trim();
        const bDurationUnit = ((b.durationUnit || b.DurationUnit || liveDurationUnit) as string).trim();
        if ((aDuration != null && aDuration !== 0) || (bDuration != null && bDuration !== 0)) {
          if (aDuration !== bDuration) {
            const fmtDur = (d: any, unit: string) =>
              d != null && d !== 0 ? `${d} ${unit}` : "not set";
            bullets.push(
              `Duration changed from ${fmtDur(aDuration, aDurationUnit)} (${fromLabel}) to ${fmtDur(bDuration, bDurationUnit)} (${toLabel}).`
            );
          }
        }

        // 15) Sponsored content — not stored in version snapshots; show current live state
        if (live) {
          const isSponsored = !!(live.isSponsored ?? live.IsSponsored);
          bullets.push(
            isSponsored
              ? "Sponsored content: This guide is currently marked as sponsored."
              : "Sponsored content: This guide is not currently sponsored."
          );
        }

        // 16) Collaboration — compare snapshots; fall back to live for old snapshots that predate collab storage
        {
          const parseJsonArray = (val: any): string[] => {
            if (!val) return [];
            if (Array.isArray(val)) return (val as any[]).map(String).filter(Boolean);
            try {
              const parsed = JSON.parse(val);
              return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
            } catch { return []; }
          };

          const resolveCollaborators = (snapshot: any, fallback: any): string[] => {
            // Try snapshot first (stored since collaboration versioning was added)
            const src = (snapshot?.isCollaboration || snapshot?.IsCollaboration) ? snapshot
                      : (fallback?.isCollaboration || fallback?.IsCollaboration) ? fallback
                      : null;
            if (!src) return [];
            const names  = parseJsonArray(src.coAuthorUserNames || src.CoAuthorUserNames);
            const emails = parseJsonArray(src.coAuthorEmails    || src.CoAuthorEmails);
            const singleName  = (src.coAuthorUserName || src.CoAuthorUserName || '').trim();
            const singleEmail = (src.coAuthorEmail    || src.CoAuthorEmail    || '').trim();
            return names.length ? names
                 : singleName   ? [singleName]
                 : emails.length ? emails
                 : singleEmail   ? [singleEmail]
                 : [];
          };

          const aCollabs = resolveCollaborators(a, live);
          const bCollabs = resolveCollaborators(b, live);
          const aIsCollab = !!(a?.isCollaboration ?? a?.IsCollaboration) || aCollabs.length > 0;
          const bIsCollab = !!(b?.isCollaboration ?? b?.IsCollaboration) || bCollabs.length > 0;

          const fmt = (collabs: string[]) =>
            collabs.length ? collabs.map(n => `@${n}`).join(", ") : "none";

          if (aIsCollab !== bIsCollab) {
            if (bIsCollab) {
              bullets.push(
                `Collaboration added in ${toLabel}${bCollabs.length ? `: ${fmt(bCollabs)}` : "."}`
              );
            } else {
              bullets.push(`Collaboration removed in ${toLabel}.`);
            }
          } else if (aIsCollab && bIsCollab) {
            const aSet = new Set(aCollabs.map(n => n.toLowerCase()));
            const bSet = new Set(bCollabs.map(n => n.toLowerCase()));
            const added   = bCollabs.filter(n => !aSet.has(n.toLowerCase()));
            const removed = aCollabs.filter(n => !bSet.has(n.toLowerCase()));
            if (added.length || removed.length) {
              if (added.length)   bullets.push(`Collaboration: ${added.map(n => `@${n}`).join(", ")} added in ${toLabel}.`);
              if (removed.length) bullets.push(`Collaboration: ${removed.map(n => `@${n}`).join(", ")} removed in ${toLabel}.`);
            } else if (aCollabs.length) {
              // Same collaborators — surface for awareness
              bullets.push(`Collaboration: Currently collaborated with ${fmt(bCollabs)}.`);
            }
          }
        }

        if (!bullets.length) {
          bullets.push("No differences detected between the selected versions.");
        }

        this.diffSummary = bullets;
        this.diffLoading = false;
      },
      error: () => {
        this.diffError = "Failed to compare selected versions.";
        this.diffLoading = false;
      },
    });
  }

  isVideo(url?: string | null): boolean {
    if (!url || typeof url !== "string") {
      return false;
    }
    const queryIndex = url.indexOf("?");
    const cleanUrl = queryIndex >= 0 ? url.substring(0, queryIndex) : url;
    const lastDot = cleanUrl.lastIndexOf(".");
    if (lastDot === -1 || lastDot === cleanUrl.length - 1) {
      return false;
    }
    const ext = cleanUrl.substring(lastDot + 1).toLowerCase();
    return this.videoFileExtensions.includes(ext);
  }

  getStepMediaUrl(step: any): string | null {
    if (!step) {
      return null;
    }
    return step.url || step.mediaUrl || step.filePath || null;
  }

  getVersionAuthor(version: VersionSummary): string {
    const rawUserName = (version?.userName || "").trim();
    if (rawUserName) {
      return rawUserName.startsWith("@") ? rawUserName : `@${rawUserName}`;
    }

    const fallback = (version?.createdBy || "").trim();
    return fallback || "Unknown";
  }

  /** Parse a JSON-encoded string[] field, returning an empty array on failure. */
  parseJsonArray(value: string | undefined | null): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value as string[];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  getCollaboratorAvatars(version: VersionSummary): { name: string; pic: string }[] {
    const pics = this.parseJsonArray(version.coAuthorProfilePicUrls);
    const names = this.parseJsonArray(version.coAuthorUserNames);
    const len = Math.max(pics.length, names.length);
    const result: { name: string; pic: string }[] = [];
    for (let i = 0; i < len; i++) {
      result.push({ name: names[i] || '', pic: pics[i] || '' });
    }
    return result;
  }

  private mapBundlePreviewModel(rawModel: any, versionNumber: number): any {
    const tryParseJson = (value: any): any => {
      if (!value || typeof value !== "string") return null;
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    };

    const pickFirst = (obj: any, keys: string[]): any => {
      for (const key of keys) {
        const val = obj?.[key];
        if (val !== null && val !== undefined && `${val}`.trim() !== "") {
          return val;
        }
      }
      return "";
    };
    const deepFindByKeys = (obj: any, keys: string[]): any => {
      if (!obj || typeof obj !== "object") return "";

      const target = new Set(keys.map((k) => k.toLowerCase()));
      const queue: any[] = [obj];
      const seen = new Set<any>();

      while (queue.length) {
        const current = queue.shift();
        if (!current || typeof current !== "object" || seen.has(current)) continue;
        seen.add(current);

        for (const key of Object.keys(current)) {
          const value = (current as any)[key];
          if (
            target.has(key.toLowerCase()) &&
            value !== null &&
            value !== undefined &&
            `${value}`.trim() !== ""
          ) {
            return value;
          }
          if (value && typeof value === "object") {
            queue.push(value);
          }
        }
      }
      return "";
    };
    const looksLikeMediaUrl = (value: any): boolean => {
      if (typeof value !== "string") return false;
      const v = value.toLowerCase().trim();
      return (
        v.startsWith("http://") ||
        v.startsWith("https://") ||
        v.includes("/mediaimage/") ||
        v.includes("/mediavideo/") ||
        v.includes(".blob.core.windows.net/")
      );
    };
    const deepFindAnyMediaUrl = (obj: any): string => {
      if (!obj || typeof obj !== "object") return "";
      const queue: any[] = [obj];
      const seen = new Set<any>();
      while (queue.length) {
        const current = queue.shift();
        if (!current || typeof current !== "object" || seen.has(current)) continue;
        seen.add(current);
        for (const key of Object.keys(current)) {
          const value = (current as any)[key];
          if (looksLikeMediaUrl(value)) {
            return value;
          }
          if (value && typeof value === "object") {
            queue.push(value);
          }
        }
      }
      return "";
    };

    const base =
      rawModel?.updatedBundle ||
      rawModel?.bundleModel ||
      rawModel?.BundleModel ||
      rawModel;
    const parsedFromString =
      tryParseJson(rawModel?.updatedBundleJson) ||
      tryParseJson(rawModel?.UpdatedBundleJson) ||
      tryParseJson(rawModel?.bundleJson) ||
      tryParseJson(rawModel?.BundleJson) ||
      tryParseJson(base);

    const source = parsedFromString || base || {};
    const flowsRaw = source.flows || source.Flows || source.bundleFlows || source.BundleFlows || [];
    const flowIdsRaw = source.flowIds || source.FlowIds || [];
    const mapFlow = (f: any) => ({
      ...f,
      rowKey: f?.rowKey || f?.RowKey || f?.id || f?.Id || "",
      title: f?.title || f?.Title || "",
      description: f?.description || f?.Description || "",
      url: f?.url || f?.Url || "",
      videoThumbNailUrl:
        f?.videoThumbNailUrl || f?.VideoThumbNailUrl || f?.videoThumbnailUrl || "",
      userName: f?.userName || f?.UserName || "",
      uniqueId: f?.uniqueId || f?.UniqueId || "",
      price: f?.price ?? f?.Price ?? 0,
      showImage: f?.showImage ?? f?.ShowImage ?? true,
    });
    const mappedFlows = Array.isArray(flowsRaw) ? flowsRaw.map(mapFlow) : [];
    const fallbackFlows =
      mappedFlows.length > 0
        ? mappedFlows
        : Array.isArray(flowIdsRaw)
        ? flowIdsRaw.map((id: any) => ({
            rowKey: `${id}`,
            title: `Flow ${id}`,
            uniqueId: `${id}`,
            url: "",
            showImage: true,
            price: 0,
          }))
        : [];

    const mappedUrl =
      pickFirst(source, [
        "url",
        "Url",
        "mediaUrl",
        "MediaUrl",
        "filePath",
        "FilePath",
        "bundleImage",
        "BundleImage",
        "coverImage",
        "CoverImage",
        "bundleImageUrl",
        "BundleImageUrl",
        "imageUrl",
        "ImageUrl",
        "thumbnailUrl",
        "ThumbnailUrl",
      ]) ||
      deepFindByKeys(source, [
        "url",
        "mediaUrl",
        "filePath",
        "bundleImage",
        "coverImage",
        "bundleImageUrl",
        "imageUrl",
      ]) ||
      deepFindAnyMediaUrl(source);

    const mappedPoster =
      pickFirst(source, [
        "videoThumbNailUrl",
        "VideoThumbNailUrl",
        "videoThumbnailUrl",
        "VideoThumbnailUrl",
        "coverPosterPageURI",
        "CoverPosterPageURI",
        "posterUrl",
        "PosterUrl",
      ]) ||
      deepFindByKeys(source, [
        "videoThumbNailUrl",
        "videoThumbnailUrl",
        "coverPosterPageURI",
        "posterUrl",
      ]);

    const uniqueIdVal =
      pickFirst(source, ["uniqueId", "UniqueId"]) || "";
    const uniqueCodeVal =
      pickFirst(source, ["uniqueCode", "UniqueCode"]) ||
      pickFirst(source, ["uniqueCodeWithoutDot", "UniqueCodeWithoutDot"]) ||
      (uniqueIdVal ? `${uniqueIdVal}`.trim() : "");

    return {
      ...source,
      rowKey: source?.rowKey || source?.RowKey || this.data.logicalId,
      title: source?.title || source?.Title || "",
      description: source?.description || source?.Description || "",
      uniqueId: uniqueIdVal || source?.uniqueId || source?.UniqueId || "",
      uniqueCode: uniqueCodeVal,
      uniqueCodeWithoutDot:
        pickFirst(source, ["uniqueCodeWithoutDot", "UniqueCodeWithoutDot"]) ||
        (uniqueCodeVal ? `${uniqueCodeVal}`.replace(/\./g, "") : "") ||
        (uniqueIdVal ? `${uniqueIdVal}`.replace(/\./g, "") : ""),
      url: mappedUrl || "",
      videoThumbNailUrl: mappedPoster || "",
      userName: source?.userName || source?.UserName || source?.ownerName || "",
      profilePic: source?.profilePic || source?.ProfilePic || source?.profilePicUrl || "",
      customerEmail: source?.customerEmail || source?.CustomerEmail || "",
      accessType: source?.accessType || source?.AccessType || "Public",
      isPaid: source?.isPaid ?? source?.IsPaid ?? false,
      price: source?.price ?? source?.Price ?? 0,
      isFlowPurchased: source?.isFlowPurchased ?? source?.IsFlowPurchased ?? false,
      flowSupportingDocumentsHyperLinkModels:
        source?.flowSupportingDocumentsHyperLinkModels ||
        source?.FlowSupportingDocumentsHyperLinkModels ||
        [],
      flowSupportingDocumentsDocumentModels:
        source?.flowSupportingDocumentsDocumentModels ||
        source?.FlowSupportingDocumentsDocumentModels ||
        [],
      flows: fallbackFlows,
      version: source?.version ?? source?.Version ?? versionNumber,
      type: source?.type || source?.Type || "Bundle",
    };
  }

  preview(version: VersionSummary): void {
    // For flows, open the real viewflow page inside a dialog so
    // the exact same layout and styling are used.
    if (this.data.entityType === "flow") {
      const compRef = this.dialog.open(ViewflowComponent, {
        panelClass: "flow-preview-pane",
        width: "80vw",
        maxWidth: "1100px",
        height: "80vh",
        minHeight: "80vh",
        maxHeight: "80vh",
      });

      compRef.componentInstance.previewLogicalId = this.data.logicalId;
      compRef.componentInstance.previewVersion = version.version;
      compRef.componentInstance.openInDialog = true;

      compRef.afterClosed().subscribe(() => {
        const hasVersionHistoryOpen = this.dialog.openDialogs.some(
          (dialogRef) => {
            const instance = dialogRef.componentInstance as any;
            return (
              instance instanceof VersionHistoryPanelComponent &&
              instance?.data?.entityType === this.data?.entityType &&
              instance?.data?.logicalId === this.data?.logicalId
            );
          }
        );

        if (!hasVersionHistoryOpen) {
          this.dialog.open(VersionHistoryPanelComponent, {
            width: "640px",
            data: { ...this.data },
          });
        }
      });
      return;
    }

    // For bundles, open the real view-bundle page in dialog (same UX style as flow preview)
    this.previewLoading = true;
    const query = new FlowQueryModel({
      BundleId: this.data.logicalId,
      userEmail: this.getCurrentUserEmail(),
    });

    forkJoin({
      versionModel: this.versioningService.getBundleByVersion(
        this.data.logicalId,
        version.version
      ),
      liveBundle: this.bundleService.getBundleById(query).pipe(
        catchError(() => of({ bundleModel: null }))
      ),
    }).subscribe({
      next: ({ versionModel, liveBundle }) => {
        this.previewLoading = false;
        if (!versionModel) {
          this.toastr.warning("Preview of this version is not available.");
          this.openBundlePreviewDialog(
            null,
            version.version,
            "Preview of this version is not available."
          );
          return;
        }
        const mappedBundleModel = this.mapBundlePreviewModel(versionModel, version.version);
        const live = liveBundle?.bundleModel || null;
        const hydrated = this.applyLiveBundleToPreview(mappedBundleModel, live);
        this.openBundlePreviewDialog(hydrated, version.version);
      },
      error: () => {
        this.toastr.warning("Preview of this version is not available.");
        this.openBundlePreviewDialog(
          null,
          version.version,
          "Preview of this version is not available."
        );
        this.previewLoading = false;
      },
    });
  }

  /**
   * Version snapshots often only store flow IDs; live bundle has urls/thumbnails for sidebar.
   * Merge live flow media onto preview flows, preserving version flow order.
   */
  private applyLiveBundleToPreview(mapped: any, live: any | null): any {
    if (!live) {
      return mapped;
    }
    const out = { ...mapped };
    const mUrl = (mapped?.url || mapped?.Url || "").toString().trim();
    const mThumb = (mapped?.videoThumbNailUrl || mapped?.VideoThumbNailUrl || "")
      .toString()
      .trim();
    if (!mUrl) {
      out.url = live.url || live.Url || "";
    }
    if (!mThumb) {
      out.videoThumbNailUrl =
        live.videoThumbNailUrl || live.VideoThumbNailUrl || "";
    }
    const uc = (out.uniqueCode || out.UniqueCode || "").toString().trim();
    const ucd = (
      out.uniqueCodeWithoutDot ||
      out.UniqueCodeWithoutDot ||
      ""
    )
      .toString()
      .trim();
    const uid = (out.uniqueId || out.UniqueId || "").toString().trim();
    if (!uc) {
      out.uniqueCode =
        live.uniqueCode ||
        live.UniqueCode ||
        (live.uniqueId || live.UniqueId || "").toString().trim() ||
        live.uniqueCodeWithoutDot ||
        live.UniqueCodeWithoutDot ||
        "";
    }
    if (!ucd) {
      out.uniqueCodeWithoutDot =
        live.uniqueCodeWithoutDot ||
        live.UniqueCodeWithoutDot ||
        (out.uniqueCode || "").toString().replace(/\./g, "") ||
        "";
    }
    if (!uid) {
      out.uniqueId = live.uniqueId || live.UniqueId || "";
    }
    out.flows = this.mergePreviewFlowsWithLive(
      mapped?.flows,
      mapped?.flowIds || mapped?.FlowIds || live?.flowIds || live?.FlowIds,
      live?.flows || live?.Flows
    );
    return out;
  }

  private mergePreviewFlowsWithLive(
    previewFlows: any[] | undefined,
    flowIdsRaw: any,
    liveFlowsRaw: any[] | undefined
  ): any[] {
    const liveFlows = Array.isArray(liveFlowsRaw) ? liveFlowsRaw : [];
    const liveMap = new Map<string, any>();
    for (const f of liveFlows) {
      const k = `${f?.rowKey ?? f?.RowKey ?? f?.id ?? f?.Id ?? ""}`.trim();
      if (k) {
        liveMap.set(k, f);
      }
    }

    const idsFromVersion = Array.isArray(flowIdsRaw)
      ? flowIdsRaw.map((id: any) => `${id ?? ""}`.trim()).filter(Boolean)
      : [];

    const baseFlows = Array.isArray(previewFlows) ? previewFlows : [];
    const ids =
      idsFromVersion.length > 0
        ? idsFromVersion
        : baseFlows
            .map((p: any) => `${p?.rowKey ?? ""}`.trim())
            .filter(Boolean);

    if (!ids.length) {
      return baseFlows;
    }

    return ids.map((id) => {
      const rowKeyStr = `${id}`.trim();
      const base =
        baseFlows.find((p: any) => `${p?.rowKey ?? ""}`.trim() === id) || {
          rowKey: id,
          title: `Flow ${id}`,
          url: "",
          videoThumbNailUrl: "",
          userName: "",
          showImage: true,
          price: 0,
        };
      const liveF = liveMap.get(id);
      if (!liveF) {
        const baseUid = (base.uniqueId || "").toString().trim();
        return {
          ...base,
          uniqueId:
            baseUid && baseUid !== rowKeyStr ? baseUid : rowKeyStr,
        };
      }
      const liveUrl = (liveF.url || liveF.Url || "").toString().trim();
      const liveThumb = (
        liveF.videoThumbNailUrl ||
        liveF.VideoThumbNailUrl ||
        liveF.videoThumbnailUrl ||
        ""
      )
        .toString()
        .trim();
      const baseUrl = (base.url || "").toString().trim();
      const placeholderTitle = `Flow ${id}`;
      const liveTitle = (liveF.title || liveF.Title || "").toString().trim();
      const liveUid = (liveF.uniqueId || liveF.UniqueId || "")
        .toString()
        .trim();
      const liveCode = (
        liveF.uniqueCode ||
        liveF.UniqueCode ||
        liveF.uniqueCodeWithoutDot ||
        liveF.UniqueCodeWithoutDot ||
        ""
      )
        .toString()
        .trim();
      const baseUid = (base.uniqueId || "").toString().trim();
      const baseUidIsRowKey = !baseUid || baseUid === rowKeyStr;
      const baseCode = (
        base.uniqueCode ||
        base.UniqueCode ||
        base.uniqueCodeWithoutDot ||
        base.UniqueCodeWithoutDot ||
        ""
      )
        .toString()
        .trim();

      return {
        ...base,
        title:
          (base.title || "").toString().trim() &&
          (base.title || "").toString().trim() !== placeholderTitle
            ? base.title
            : liveTitle || base.title || placeholderTitle,
        url: baseUrl || liveUrl,
        videoThumbNailUrl:
          (base.videoThumbNailUrl || "").toString().trim() || liveThumb,
        userName: (base.userName || "").toString().trim()
          ? base.userName
          : liveF.userName || liveF.UserName || "",
        uniqueCode: baseCode || liveCode,
        uniqueCodeWithoutDot:
          (base.uniqueCodeWithoutDot || base.UniqueCodeWithoutDot || "")
            .toString()
            .trim() ||
          (liveF.uniqueCodeWithoutDot || liveF.UniqueCodeWithoutDot || "")
            .toString()
            .trim() ||
          (baseCode || liveCode ? `${baseCode || liveCode}`.replace(/\./g, "") : ""),
        uniqueId: baseUidIsRowKey
          ? liveUid || baseUid || rowKeyStr
          : baseUid || liveUid || rowKeyStr,
        price:
          base.price != null && base.price !== ""
            ? base.price
            : liveF.price ?? liveF.Price ?? 0,
        showImage:
          base.showImage !== undefined && base.showImage !== null
            ? base.showImage
            : liveF.showImage ?? liveF.ShowImage ?? true,
      };
    });
  }

  private openBundlePreviewDialog(
    mappedBundleModel: any,
    versionNumber: number,
    previewUnavailableMessage?: string
  ): void {
    const compRef = this.dialog.open(ViewBundleComponent, {
      panelClass: "flow-preview-pane",
      width: "80vw",
      maxWidth: "1100px",
      height: "80vh",
      minHeight: "80vh",
      maxHeight: "80vh",
      data: {
        openInDialog: true,
        previewVersion: versionNumber,
        previewBundleData: mappedBundleModel,
        previewUnavailableMessage: previewUnavailableMessage || null,
      },
    });

    // Keep fallback assignment in case data injection path is unavailable.
    compRef.componentInstance.openInDialog = true;
    compRef.componentInstance.previewVersion = versionNumber;
    compRef.componentInstance.previewBundleData = mappedBundleModel;
    compRef.componentInstance.previewUnavailableMessage =
      previewUnavailableMessage || null;

    // Keep version list modal available after bundle preview closes.
    compRef.afterClosed().subscribe(() => {
      const hasVersionHistoryOpen = this.dialog.openDialogs.some((dialogRef) => {
        const instance = dialogRef.componentInstance as any;
        return (
          instance instanceof VersionHistoryPanelComponent &&
          instance?.data?.entityType === this.data?.entityType &&
          instance?.data?.logicalId === this.data?.logicalId
        );
      });

      if (!hasVersionHistoryOpen) {
        this.dialog.open(VersionHistoryPanelComponent, {
          width: "640px",
          data: { ...this.data },
        });
      }
    });
  }

  private getCurrentUserEmail(): string {
    try {
      const user = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
      return user?.emailAddress || "";
    } catch {
      return "";
    }
  }

  restoreVersion(version: VersionSummary): void {
    if (version.isCurrent) {
      return;
    }

    const confirmRef = this.dialog.open(VersionRestoreConfirmDialogComponent, {
      width: "480px",
      maxWidth: "95vw",
      autoFocus: "dialog",
      data: {
        entityType: this.data.entityType,
        action: "restore" as const,
        version: version.version,
      },
    });

    confirmRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.currentRestoreVersion = version.version;

      const source =
        this.data.entityType === "flow"
          ? this.versioningService.restoreFlowVersion(
              this.data.logicalId,
              version.version
            )
          : this.versioningService.restoreBundleVersion(
              this.data.logicalId,
              version.version
            );

      source.subscribe({
        next: () => {
          this.currentRestoreVersion = null;
          this.toastr.success(
            `Restored from v${version.version}. A new current version was created.`
          );
          this.dialogRef.close({
            action: "restore",
            version: version.version,
            logicalId: this.data.logicalId,
            entityType: this.data.entityType,
          } as VersionHistoryDialogResult);
        },
        error: (err) => {
          this.currentRestoreVersion = null;
          const message = err?.error || "Failed to restore version.";
          this.toastr.error(
            typeof message === "string" ? message : "Failed to restore version."
          );
        },
      });
    });
  }

  deleteVersion(version: VersionSummary): void {
    const isCurrentVersion = !!version.isCurrent;
    const liveRow =
      this.versions.find((v) => v.isCurrent) ?? this.versions[0] ?? null;
    const liveVersion = liveRow?.version ?? null;

    const remainingNums = this.versions
      .map((v) => v.version)
      .filter((vn) => vn !== version.version);
    const nextMainVersion =
      isCurrentVersion && remainingNums.length > 0
        ? Math.max(...remainingNums)
        : null;

    const confirmRef = this.dialog.open(VersionRestoreConfirmDialogComponent, {
      width: "480px",
      maxWidth: "95vw",
      autoFocus: "dialog",
      data: {
        entityType: this.data.entityType,
        action: "delete" as const,
        version: version.version,
        isCurrentVersion,
        nextMainVersion,
        liveVersion,
      },
    });

    confirmRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.currentDeleteVersion = version.version;

      const source =
        this.data.entityType === "flow"
          ? this.versioningService.deleteFlowVersion(
              this.data.logicalId,
              version.version
            )
          : this.versioningService.deleteBundleVersion(
              this.data.logicalId,
              version.version
            );

      source.subscribe({
        next: () => {
          this.currentDeleteVersion = null;
          this.toastr.success(`Version v${version.version} deleted.`);
          this.selectedVersions = this.selectedVersions.filter(
            (v) => v.version !== version.version
          );
          this.diffSummary = [];
          this.diffError = null;
          this.dialogRef.close({
            action: "delete",
            version: version.version,
            logicalId: this.data.logicalId,
            entityType: this.data.entityType,
          } as VersionHistoryDialogResult);
        },
        error: (err) => {
          this.currentDeleteVersion = null;
          const message = err?.error || "Failed to delete version.";
          this.toastr.error(message);
        },
      });
    });
  }

  /** Classify a diff bullet message so the template can colour-code it. */
  classifyBullet(msg: string): 'added' | 'removed' | 'changed' | 'info' {
    const s = msg.toLowerCase();
    if (s.startsWith('no differences')) return 'info';
    if (
      s.includes('was added') || s.includes('were added') ||
      s.includes('added in') || s.includes('added:') ||
      s.includes('added —') || s.includes('was set to')
    ) return 'added';
    if (
      s.includes('was removed') || s.includes('were removed') ||
      s.includes('removed in') || s.includes('removed:') ||
      s.includes('removed —')
    ) return 'removed';
    return 'changed';
  }

  close(): void {
    this.dialogRef.close();
  }
}

